import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  position?: 'left' | 'right';
  width?: string;
  expandable?: boolean;
  expanded?: boolean;
  onExpandToggle?: () => void;
  headerActions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export default function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  position = 'right',
  width = 'w-[33vw]',
  expandable = false,
  expanded = false,
  onExpandToggle,
  headerActions,
  children,
  className = '',
}: Readonly<DrawerProps>) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ESC 키 핸들링
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      // 드로워가 열릴 때
      console.log('🚪 Drawer opening...');
      setIsVisible(true);

      // 다음 프레임에서 애니메이션 시작
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log('🎬 Starting animation...');
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);

      const timer = setTimeout(() => {
        console.log('🗑️ Drawer removed from DOM');
        setIsVisible(false);
      }, 300); // transition duration과 일치
      return () => clearTimeout(timer);
    }

    setIsMobile(window.innerWidth < 640);
  }, [isOpen]);

  if (!isVisible) return null;

  const positionClasses = {
    left: {
      container: 'left-0',
      baseTransform: isAnimating ? 'translateX(0)' : 'translateX(-100%)',
    },
    right: {
      container: 'right-0',
      baseTransform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
    },
  };

  const currentPosition = positionClasses[position];
  const finalWidth = isMobile
    ? 'w-full'
    : expandable && expanded
      ? 'w-full'
      : width;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-[999] transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 ${currentPosition.container} h-full bg-white shadow-2xl border-gray-200 z-[9999] overflow-y-auto transition-all duration-300 ease-out ${finalWidth} ${
          position === 'left' ? 'border-r' : 'border-l'
        } ${className}`}
        style={{
          transform: currentPosition.baseTransform,
        }}
      >
        {/* Header */}
        {(title || subtitle || expandable || headerActions) && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-b border-gray-200 bg-white sticky top-0 z-10 space-y-3 sm:space-y-0">
            <div className="flex items-center justify-between sm:contents">
              <div
                className={cn(
                  "flex-1 text-center sm:text-left 'text-xl sm:text-2xl font-bold text-gray-900 truncate ",
                  expanded && expanded && 'max-w-full'
                )}
              >
                {title && <h2 className="w-full truncate">{title}</h2>}
                {subtitle && (
                  <p className="text-base sm:text-lg text-gray-600 mt-1">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer sm:hidden"
                title="닫기"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line"></i>
                </div>
              </button>
            </div>

            {/* Header Actions - 모바일에서는 아래로 줄바꿈 */}
            {(headerActions || (expandable && onExpandToggle)) && (
              <div className="flex items-center justify-center sm:justify-end space-x-2">
                {headerActions}
                {expandable && onExpandToggle && (
                  <button
                    onClick={onExpandToggle}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer hidden sm:block"
                    title={expanded ? '축소' : '확대'}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i
                        className={
                          expanded
                            ? 'ri-contract-left-line'
                            : 'ri-expand-diagonal-line'
                        }
                      />
                    </div>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer hidden sm:block"
                  title="닫기"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={`${
            expandable && expanded
              ? 'p-6 space-y-6 max-w-6xl mx-auto'
              : 'p-6 space-y-6'
          }`}
        >
          {children}
        </div>
      </div>
    </>
  );
}
